import { PaycheckRoutingStatus } from '@fruitful/income-split';

import {
    type Member,
    type MembershipDetails,
    type Paycheck,
    type PaycheckRoutingConfig,
    PaycheckRoutingConfigStatus,
    type PaycheckRoutingRule,
    PaycheckRoutingRuleType,
    type TransformedPaycheckRoutingConfig,
} from '../entityTypes';

export const getSoloOrJointMemberName = (
    members: Array<Partial<Member> | undefined> | undefined,
) => {
    if (members == null) {
        return null;
    }
    if (members.length === 1) {
        // Solo Member
        return members[0]?.firstName ?? '[Member]';
    }
    if (members.length === 2) {
        // Joint Members
        return `${members[0]?.firstName ?? '[Member 1]'} and ${members[1]?.firstName ?? '[Member 2]'}`;
    }
    return null;
};

export const transformRule = (
    rule: PaycheckRoutingRule,
): PaycheckRoutingRule => {
    return {
        ...rule,
        amount: parseFloat((rule.amount / 100).toFixed(2)),
    };
};

export const transformConfigs = (
    paycheckRoutingConfigs: PaycheckRoutingConfig[],
) => {
    return paycheckRoutingConfigs.map((config) => {
        return {
            ...config,
            rules: {
                primary:
                    config?.rules
                        ?.filter(
                            (rule) =>
                                rule.type !==
                                PaycheckRoutingRuleType.PERCENTAGE,
                        )
                        .map(transformRule)
                        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) ?? [],
                remaining:
                    config?.rules
                        ?.filter(
                            (rule) =>
                                rule.type ===
                                PaycheckRoutingRuleType.PERCENTAGE,
                        )
                        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) ?? [],
            },
        };
    });
};

const paycheckMatchesRoutingConfig = (
    paycheck: Paycheck,
    routingConfig: TransformedPaycheckRoutingConfig,
) => {
    return (
        paycheck.accountId === routingConfig.accountId &&
        paycheck.originator === routingConfig.originator &&
        paycheck.receiver === routingConfig.receiver
    );
};

export const findMatchingRoutingConfigForPaycheck = (
    paycheck: Paycheck,
    routingConfigs: TransformedPaycheckRoutingConfig[],
) => {
    return routingConfigs.find((routingConfig) =>
        paycheckMatchesRoutingConfig(paycheck, routingConfig),
    );
};

export const membershipHasHadIncomeSplitWithTies = (
    membershipDetails: MembershipDetails,
): boolean => {
    return membershipDetails.incomeSplitConfig?.status !== undefined;
};

export const membershipHasHadIncomeSplitV0 = (
    membershipDetails: MembershipDetails,
): boolean => {
    if (membershipDetails.paycheckRoutingConfigs.length > 0) {
        const hasIncomeSplitV0 = membershipDetails.paycheckRoutingConfigs.some(
            (config) => config.originator === null && config.receiver === null,
        );
        return hasIncomeSplitV0;
    } else {
        return false;
    }
};

export const membershipHasHadPaycheckSplit = (
    membershipDetails: MembershipDetails,
): boolean => {
    return membershipDetails.paycheckRoutingConfigs.length > 0;
};

export const showIncomeSplit = (
    membershipDetails: MembershipDetails,
): boolean => {
    const incomeSplitConfigIsActive =
        membershipDetails.incomeSplitConfig?.status ===
        PaycheckRoutingStatus.ACTIVE;
    if (incomeSplitConfigIsActive) {
        return true;
    }

    const activePaycheckRoutingConfigs =
        membershipDetails.paycheckRoutingConfigs.filter(
            (config) => config.status === PaycheckRoutingConfigStatus.ACTIVE,
        );

    if (activePaycheckRoutingConfigs.length === 0) {
        // No active configs --> income split
        return true;
    }

    // Multiple active configs --> paycheck split
    if (activePaycheckRoutingConfigs.length > 1) {
        return false;
    }

    if (activePaycheckRoutingConfigs.length === 1) {
        if (
            activePaycheckRoutingConfigs[0]?.originator !== null ||
            activePaycheckRoutingConfigs[0]?.receiver !== null
        ) {
            // Only one active config and it has an originator or receiver --> paycheck split
            return false;
        }
    }

    return true;
};
